/**
 * <tf-skeleton> — Placeholder loading reattivo.
 *
 * Sostituisce le N implementazioni inline di skeleton sparse nelle pagine.
 *
 * Uso HTML (markup standalone):
 *   <tf-skeleton type="text" lines="3"></tf-skeleton>
 *   <tf-skeleton type="card"></tf-skeleton>
 *   <tf-skeleton type="table" rows="5" cols="4"></tf-skeleton>
 *   <tf-skeleton type="avatar" size="md"></tf-skeleton>
 *
 * Uso programmatico (con skelRace):
 *   const data = await tfSkelRace(fetchData(), 350);
 *
 * Attributes:
 *   - type: "text" | "card" | "table" | "avatar" | "list" (default: "text")
 *   - lines: number (per text) — default 3
 *   - rows: number (per table) — default 5
 *   - cols: number (per table) — default 3
 *   - size: "sm" | "md" | "lg" (per avatar) — default md
 *
 * @since v9.16 (Sprint 3)
 */

const AVATAR_SIZES = { sm: 'h-8 w-8', md: 'h-10 w-10', lg: 'h-14 w-14' };

export class TfSkeletonElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['type', 'lines', 'rows', 'cols', 'size'];
  }

  connectedCallback(): void {
    this._render();
  }

  attributeChangedCallback(): void {
    if (this.isConnected) this._render();
  }

  private _render(): void {
    const type = this.getAttribute('type') || 'text';
    switch (type) {
      case 'card':
        this._renderCard();
        break;
      case 'table':
        this._renderTable();
        break;
      case 'avatar':
        this._renderAvatar();
        break;
      case 'list':
        this._renderList();
        break;
      case 'text':
      default:
        this._renderText();
    }
    this._injectStyles();
  }

  private _renderText(): void {
    const lines = parseInt(this.getAttribute('lines') || '3', 10);
    const items = Array.from({ length: lines }, (_, i) => {
      const widthClass = i === lines - 1 ? 'w-2/3' : 'w-full';
      return `<div class="tf-skel-bar h-3 rounded-full bg-slate-200 ${widthClass}"></div>`;
    });
    this.innerHTML = `<div class="space-y-2.5">${items.join('')}</div>`;
  }

  private _renderCard(): void {
    this.innerHTML = `
      <div class="tf-skel-card rounded-xl border border-slate-200 bg-white p-4">
        <div class="flex items-center gap-3 mb-3">
          <div class="tf-skel-bar h-10 w-10 rounded-full bg-slate-200"></div>
          <div class="flex-1 space-y-2">
            <div class="tf-skel-bar h-3 w-2/3 rounded-full bg-slate-200"></div>
            <div class="tf-skel-bar h-2 w-1/3 rounded-full bg-slate-100"></div>
          </div>
        </div>
        <div class="space-y-2">
          <div class="tf-skel-bar h-3 rounded-full bg-slate-200"></div>
          <div class="tf-skel-bar h-3 w-4/5 rounded-full bg-slate-200"></div>
        </div>
      </div>
    `;
  }

  private _renderTable(): void {
    const rows = parseInt(this.getAttribute('rows') || '5', 10);
    const cols = parseInt(this.getAttribute('cols') || '3', 10);
    const rowsHtml = Array.from({ length: rows })
      .map(() => {
        const cells = Array.from({ length: cols })
          .map(
            () =>
              `<td class="px-4 py-3"><div class="tf-skel-bar h-3 rounded-full bg-slate-200"></div></td>`
          )
          .join('');
        return `<tr>${cells}</tr>`;
      })
      .join('');
    this.innerHTML = `<table class="w-full"><tbody>${rowsHtml}</tbody></table>`;
  }

  private _renderAvatar(): void {
    const size = (this.getAttribute('size') as keyof typeof AVATAR_SIZES) || 'md';
    const sizeClass = AVATAR_SIZES[size] || AVATAR_SIZES.md;
    this.innerHTML = `<div class="tf-skel-bar ${sizeClass} rounded-full bg-slate-200"></div>`;
  }

  private _renderList(): void {
    const items = parseInt(this.getAttribute('lines') || '4', 10);
    const itemsHtml = Array.from({ length: items })
      .map(
        () => `
      <div class="flex items-center gap-3">
        <div class="tf-skel-bar h-9 w-9 rounded-full bg-slate-200"></div>
        <div class="flex-1 space-y-1.5">
          <div class="tf-skel-bar h-3 w-2/3 rounded-full bg-slate-200"></div>
          <div class="tf-skel-bar h-2 w-1/3 rounded-full bg-slate-100"></div>
        </div>
      </div>
    `
      )
      .join('');
    this.innerHTML = `<div class="space-y-3">${itemsHtml}</div>`;
  }

  private _injectStyles(): void {
    if (document.getElementById('tf-skeleton-styles')) return;
    const style = document.createElement('style');
    style.id = 'tf-skeleton-styles';
    style.textContent = `
      .tf-skel-bar {
        background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
        background-size: 200% 100%;
        animation: tfSkelShimmer 1.5s ease-in-out infinite;
      }
      @keyframes tfSkelShimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @media (prefers-reduced-motion: reduce) {
        .tf-skel-bar { animation: none; background: #e2e8f0; }
      }
    `;
    document.head.appendChild(style);
  }
}

/**
 * Race helper: ritorna la promise OR uno skeleton se la promise tarda > minMs.
 *
 * @example
 *   const data = await tfSkelRace(fetchData(), 350);
 *   // se fetchData() risolve in <350ms → ritorna data subito
 *   // altrimenti → mostra skeleton e aspetta
 */
export function skelRace<T>(promise: Promise<T>, minMs = 350): Promise<T> {
  return new Promise((resolve, reject) => {
    let resolved = false;
    let timeoutFired = false;

    const timeout = setTimeout(() => {
      timeoutFired = true;
      // Trigger skeleton via custom event
      window.dispatchEvent(new CustomEvent('tf-skel-show'));
    }, minMs);

    promise
      .then((value) => {
        resolved = true;
        clearTimeout(timeout);
        if (timeoutFired) {
          window.dispatchEvent(new CustomEvent('tf-skel-hide'));
        }
        resolve(value);
      })
      .catch((err) => {
        resolved = true;
        clearTimeout(timeout);
        if (timeoutFired) {
          window.dispatchEvent(new CustomEvent('tf-skel-hide'));
        }
        reject(err);
      });

    // Dummy reference to avoid unused warning
    if (resolved) return;
  });
}

// Auto-register
if (typeof customElements !== 'undefined' && !customElements.get('tf-skeleton')) {
  customElements.define('tf-skeleton', TfSkeletonElement);
}
